/**
 * Preload Policy for Carousel Images
 * 
 * Balances performance (fast swipe) with bandwidth (minimal requests).
 * 
 * Rules:
 * - Initial load: Only first image (LCP optimization)
 * - After first interaction: Preload next N slides ahead
 * - Always keep: Current slide + minimum ahead loaded
 * 
 * This prevents shimmer flash during normal swiping while
 * avoiding unnecessary bandwidth on page load.
 */

export const PRELOAD_POLICY = {
  /** Images to preload ahead after user interacts */
  LOOKAHEAD_AFTER_INTERACTION: 2,
  /** Minimum images to keep loaded (current + this many ahead) */
  MIN_LOADED_AHEAD: 1,
} as const;

/**
 * Determines if an image should be eagerly loaded based on preload policy.
 */
export function shouldEagerLoad(
  index: number,
  activeIndex: number,
  hasInteracted: boolean,
  isFirstImage: boolean
): boolean {
  // First image always eager (LCP)
  if (isFirstImage) return true;

  // Before interaction: only load first image
  if (!hasInteracted) return false;

  // After interaction: load current + next N slides
  const lookahead = PRELOAD_POLICY.LOOKAHEAD_AFTER_INTERACTION;
  return index >= activeIndex && index <= activeIndex + lookahead;
}
